/// <amd-module name="Analytics/Boards/RollupQueries/RollupQuery" />
import { WitFieldUtilities } from "Analytics/Boards/BoardsQueries/WitFieldUtilities";
import { AnalyticsCacheableQueryBase } from "Analytics/Common/AnalyticsCacheableQueryBase";
import { BatchRequestMode, ODataQueryOptions, SyncDateCondition } from "Analytics/Common/ODataQueryOptions";
import { latestODataVersion } from "Analytics/Common/Utilities/QueryUtilities";
import { RollupAggregation, RollupCalculationSettings, RollupType } from "./Contracts";

export interface WorkItemsRecency {
    workItemsChangedUtcTimestamp?: number;
    workItemLinksChangedUtcTimestamp?: number;
}

export interface RollupDescendantDetails {
    isCompleted: boolean;
    Count?: number;
    Sum?: number;
}

export interface RollupQueryWorkItemResult {
    WorkItemId: number;
    Descendants: RollupDescendantDetails[];
}

export interface RollupQueryResult {
    syncDates: WorkItemsRecency;
    results: RollupQueryWorkItemResult[];
}

export class RollupQuery extends AnalyticsCacheableQueryBase<RollupQueryResult> {
    private static readonly commandName = "RollupQuery";
    private static readonly axServerPageSize = 1000;

    private static generateQueryOptions(
        workItemIds: number[],
        rollupMetric: RollupCalculationSettings,
        projectId: string,
        backlogWorkItemTypes?: string[],
        syncDateConditions?: SyncDateCondition[]
    ): ODataQueryOptions {
        const filterClause = RollupQuery.getMultiFilterClause("WorkItemId", workItemIds);
        const expandClause = `Descendants(${RollupQuery.getDescendantsApplyClause(rollupMetric, backlogWorkItemTypes)})`;
        return {
            entityType: "WorkItems",
            oDataVersion: latestODataVersion,
            project: projectId,
            batchRequestMode: BatchRequestMode.Enabled,
            $filter: filterClause,
            $select: "WorkItemId",
            $expand: expandClause,
            getProviderSyncDates: true,
            expectSinglePage: workItemIds.length < RollupQuery.axServerPageSize,
            syncDateConditions,
            getBatchResponseInnerHeaders: !!syncDateConditions
        };
    }

    private static getMultiFilterClause(itemName: string, items?: number[] | string[]): string {
        let filterClause = "";
        items &&
            items.forEach((item: string | number | number[]) => {
                if (typeof item === "string") {
                    item = `'${item}'`;
                }
                if (!filterClause) {
                    filterClause = `${itemName} eq ${item}`;
                } else {
                    filterClause += ` or ${itemName} eq ${item}`;
                }
            });
        return filterClause;
    }

    private static getDescendantsApplyClause(rollupMetric: RollupCalculationSettings, backlogWorkItemTypes?: string[]): string {
        const workItemTypeString = "WorkItemType";
        let workItemTypeFilter = "";
        if (rollupMetric.backlogFilter) {
            workItemTypeFilter = RollupQuery.getMultiFilterClause(workItemTypeString, backlogWorkItemTypes);
        } else if (rollupMetric.workItemTypeFilter) {
            workItemTypeFilter = RollupQuery.getMultiFilterClause(workItemTypeString, [rollupMetric.workItemTypeFilter]);
        }

        const filterClauseEnd = workItemTypeFilter ? ` and ${workItemTypeFilter})` : ")";
        const filterClause = "$apply=filter(StateCategory ne null" + filterClauseEnd;
        const aggregateClause = RollupQuery.getAggregateClause(rollupMetric);

        if (rollupMetric.type === RollupType.Progress) {
            const computeClause = "compute(StateCategory eq 'Completed' as isCompleted)";
            const groupByClause = `groupby((isCompleted), ${aggregateClause})`;

            return [filterClause, computeClause, groupByClause].join("/");
        } else {
            return [filterClause, aggregateClause].join("/");
        }
    }

    private static getAggregateClause(rollupMetric: RollupCalculationSettings): string {
        let aggregateClause = "aggregate(";
        switch (rollupMetric.aggregation) {
            case RollupAggregation.Sum:
                if (!rollupMetric.aggregationField) {
                    throw new Error("Missing aggregation field");
                }
                const aggregationFieldODataPropertyName = WitFieldUtilities.getFieldODataPropertyName(rollupMetric.aggregationField);
                aggregateClause += `${aggregationFieldODataPropertyName} with sum as Sum)`;
                return aggregateClause;
            case RollupAggregation.Count:
            default:
                aggregateClause += "$count as Count)";
                return aggregateClause;
        }
    }

    constructor(
        private workItemIds: number[],
        private rollupMetric: RollupCalculationSettings,
        private projectId: string,
        private backlogWorkItemTypes?: string[],
        private syncDateConditions?: SyncDateCondition[]
    ) {
        super(
            RollupQuery.commandName,
            RollupQuery.generateQueryOptions(workItemIds, rollupMetric, projectId, backlogWorkItemTypes, syncDateConditions)
        );
    }

    public getQueryName() {
        const idString = JSON.stringify(this.workItemIds);
        const rollupMetricString = JSON.stringify(this.rollupMetric);
        return `RollupQuery${idString}${rollupMetricString}`;
    }

    protected interpretQueryResults(data: { value: RollupQueryWorkItemResult[]; "@vsts.warnings": string[] }): RollupQueryResult {
        const result: RollupQueryResult = {
            results: data.value,
            syncDates: {}
        };

        const ProviderSyncDatesWarning = "ProviderSyncDates:";
        const warnings: string[] = data["@vsts.warnings"];
        if (warnings) {
            let syncDatesPayload = "";
            warnings.some((warning) => {
                if (warning.indexOf(ProviderSyncDatesWarning) === 0) {
                    syncDatesPayload = warning.substring(ProviderSyncDatesWarning.length);
                    return true;
                }
            });

            let syncDates: { ProviderTableName: string; ProviderSyncDate: string }[] = [];
            let minWorkItemsSyncUtcTimestamp: number | null = null;
            let minWorkItemLinksSyncUtcTimestamp: number | null = null;
            try {
                syncDates = JSON.parse(syncDatesPayload);
            } catch {
                // no-op if provider dates were not returned
            }
            for (const syncDate of syncDates) {
                // Parse returns epoch number without time zone information
                // Since AX sync dates data is returned in UTC, that's what all we need
                const parsedUtcTimestamp = Date.parse(syncDate.ProviderSyncDate);
                if (syncDate.ProviderTableName === "WorkItemRevision") {
                    minWorkItemsSyncUtcTimestamp = Math.min(
                        parsedUtcTimestamp,
                        minWorkItemsSyncUtcTimestamp ? minWorkItemsSyncUtcTimestamp : parsedUtcTimestamp
                    );
                } else if (syncDate.ProviderTableName === "WorkItemLink") {
                    minWorkItemLinksSyncUtcTimestamp = Math.min(
                        parsedUtcTimestamp,
                        minWorkItemLinksSyncUtcTimestamp ? minWorkItemLinksSyncUtcTimestamp : parsedUtcTimestamp
                    );
                }
            }
            if (minWorkItemsSyncUtcTimestamp && minWorkItemsSyncUtcTimestamp > 0) {
                // Temporary >0 check since analytics is not returning null correctly
                result.syncDates.workItemsChangedUtcTimestamp = minWorkItemsSyncUtcTimestamp;
            }

            if (minWorkItemLinksSyncUtcTimestamp && minWorkItemLinksSyncUtcTimestamp > 0) {
                // Temporary >0 check since analytics is not returning null correctly
                result.syncDates.workItemLinksChangedUtcTimestamp = minWorkItemLinksSyncUtcTimestamp;
            }
        }

        return result;
    }

    public inspectResponseFormat(data: { value: any; responseStatus: any }): void {
        /** 304 is a special response code indicating that the sync dates conditions we passed up were not met.
         *  In this case we want to accept the responsxe as valid even though we have no body data payload.
         */
        if (!data || !data.responseStatus || data.responseStatus.StatusCode !== "304") {
            super.inspectResponseFormat(data);
        }
    }
}
